/**
 * Posters/ads (`media_assets`), the composed broadcast tool, and the
 * system-status broadcast.
 *
 * Everything published here goes to two audiences (2026-08-26): a direct
 * message to every real player, and one post to the public Arif Bingo channel
 * for its subscribers. The channel supplements the direct messages rather than
 * replacing them — the two audiences barely overlap, since a DM reaches players
 * who subscribe to nothing and a channel post reaches people who have not
 * installed the game. Channel posting is silently off until
 * `CHANNEL_TELEGRAM_CHAT_ID` is set.
 *
 * Three kinds share the media list: `join_invite` (the poster sent
 * automatically before every Saturday/Sunday Arif round — see
 * `runArifInviteScan`/`runArifPreStartInvite`), `ad` (the pool
 * `runAdBroadcast` picks a random one from, on a fixed daily schedule — see
 * `AD_BROADCAST_TIMES` in `ops/scheduler.ts`), and `winner_generated` (the
 * real per-round winner poster — read-only here, written only by
 * `winnerPoster.ts` at settlement, never uploaded).
 *
 * The broadcast composer (message + optional media, sent now or scheduled)
 * is a separate tool from the ad pool — see `domain/broadcasts/index.ts`'s
 * own comment on why it isn't just another `media_assets` row.
 *
 * The API guards uploads/activate/delete with `media_asset.manage` and both
 * broadcast tools with `system.notice` — both currently held by `operator`
 * and `superadmin` (see `PERMISSIONS`). A role without either sees the list
 * but not the controls, the same "server decides, this decides what to
 * render" rule every other screen here follows.
 */

import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { ConfirmButton, ErrorBanner, Field, Spinner, Empty } from '../components/ui.tsx';
import type { AdminView, BroadcastView, MediaAssetKind, MediaAssetView } from '../api/types.ts';

const KIND_LABELS: Record<MediaAssetKind, string> = {
  join_invite: 'Join invite',
  ad: 'Ad pool',
  winner_generated: 'Winner poster (generated)',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function Posters({ me }: { me: AdminView }) {
  const [assets, setAssets] = useState<MediaAssetView[] | null>(null);
  const [broadcasts, setBroadcasts] = useState<BroadcastView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<Exclude<MediaAssetKind, 'winner_generated'>>('join_invite');
  const [caption, setCaption] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');

  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastFile, setBroadcastFile] = useState<File | null>(null);
  const [broadcastSchedule, setBroadcastSchedule] = useState('');

  const manage = can(me.role, 'media_asset.manage');
  const canNotice = can(me.role, 'system.notice');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [{ assets: nextAssets }, broadcastsResult] = await Promise.all([
        api.mediaAssets(),
        canNotice ? api.broadcasts() : Promise.resolve({ broadcasts: [] as BroadcastView[] }),
      ]);
      setAssets(nextAssets);
      setBroadcasts(broadcastsResult.broadcasts);
    } catch (err) {
      setError(err);
    }
  }, [canNotice]);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(): Promise<void> {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadMediaAsset(file, kind, caption.trim() || undefined);
      setFile(null);
      setCaption('');
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(asset: MediaAssetView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.setMediaAssetActive(asset.id, !asset.active);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(asset: MediaAssetView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.deleteMediaAsset(asset.id);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function saveCaption(asset: MediaAssetView, caption: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateMediaAsset(asset.id, { caption: caption.trim() || null });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** `scheduledAt: null` clears the schedule (the row's own "Clear" action). */
  async function saveSchedule(asset: MediaAssetView, scheduledAt: string | null): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateMediaAsset(asset.id, {
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function replaceFile(asset: MediaAssetView, file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.replaceMediaAsset(asset.id, file);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function postNow(asset: MediaAssetView): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.postMediaAssetNow(asset.id);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendNotice(status: 'down' | 'up'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.systemNotice(status, noticeMessage.trim() || undefined);
      setNoticeMessage('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function sendBroadcast(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const scheduledAt = broadcastSchedule ? new Date(broadcastSchedule).toISOString() : null;
      await api.createBroadcast(broadcastMessage.trim(), broadcastFile, scheduledAt);
      setBroadcastMessage('');
      setBroadcastFile(null);
      setBroadcastSchedule('');
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const grouped = useMemo(() => {
    const groups: Record<MediaAssetKind, MediaAssetView[]> = {
      join_invite: [],
      ad: [],
      winner_generated: [],
    };
    for (const a of assets ?? []) groups[a.kind].push(a);
    // Generated fresh every Arif win, auto-sent to the ops group now
    // (2026-08-24) — the dashboard doesn't need to keep piling up every one
    // ever made, just enough to spot-check the latest. `api.mediaAssets()`
    // already returns newest-first.
    groups.winner_generated = groups.winner_generated.slice(0, 2);
    return groups;
  }, [assets]);

  return (
    <section>
      <header className="pagehead">
        <h1>Posters &amp; broadcasts</h1>
      </header>

      <ErrorBanner error={error} />

      {canNotice ? (
        <div className="card">
          <h2>System status</h2>
          <p className="muted">
            Broadcasts a Telegram message to every real player. There is no automatic
            detection — use this when something is actually wrong, and again once it's fixed.
          </p>
          <Field label="Message" hint="Optional — shown along with the standard notice text.">
            <input
              className="input"
              value={noticeMessage}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNoticeMessage(e.target.value)}
            />
          </Field>
          <div className="actions">
            <ConfirmButton
              className="button button--danger"
              confirm="Tell every player something is down right now?"
              disabled={busy}
              onConfirm={() => void sendNotice('down')}
            >
              Mark system down
            </ConfirmButton>
            <ConfirmButton
              className="button button--primary"
              confirm="Tell every player things are back to normal?"
              disabled={busy}
              onConfirm={() => void sendNotice('up')}
            >
              Mark system back up
            </ConfirmButton>
          </div>

          <div className="divider" />
          <h3>Broadcast</h3>
          <p className="muted">
            A message to every real player — optionally with an image or video attached.
            Send it right now, or pick a date and time to send it later.
          </p>
          <Field label="Message" hint="Required. Shown to every player exactly as written.">
            <textarea
              className="input"
              rows={3}
              value={broadcastMessage}
              disabled={busy}
              placeholder="e.g. Arif Bingo starts in 30 minutes — today’s jackpot is 120,000 ETB!"
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setBroadcastMessage(e.target.value)
              }
            />
          </Field>
          <Field label="Image or video" hint="Optional. JPEG, PNG, GIF, or MP4.">
            <input
              className="input"
              type="file"
              accept="image/jpeg,image/png,image/gif,video/mp4"
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setBroadcastFile(e.target.files?.[0] ?? null)
              }
            />
          </Field>
          <Field label="Send at" hint="Optional. Leave blank to send immediately.">
            <input
              type="datetime-local"
              className="input"
              value={broadcastSchedule}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setBroadcastSchedule(e.target.value)}
            />
          </Field>
          <div className="actions">
            <ConfirmButton
              className="button button--primary"
              confirm={
                broadcastSchedule
                  ? `Schedule this for ${new Date(broadcastSchedule).toLocaleString()}?`
                  : 'Send this to every real player right now?'
              }
              disabled={busy || broadcastMessage.trim().length === 0}
              onConfirm={() => void sendBroadcast()}
            >
              {broadcastSchedule ? 'Schedule' : 'Send now'}
            </ConfirmButton>
          </div>

          {broadcasts && broadcasts.length > 0 ? (
            <>
              <div className="divider" />
              <h3>Recent broadcasts</h3>
              <ul className="broadcastlist">
                {broadcasts.map((b) => (
                  <li key={b.id} className="broadcastlist__item">
                    <div>{b.message}</div>
                    <div className="muted small">
                      {b.sentAt
                        ? `Sent ${formatDateTime(b.sentAt)}`
                        : b.scheduledAt
                          ? `Scheduled for ${formatDateTime(b.scheduledAt)}`
                          : 'Pending'}
                      {b.mediaAssetId ? ' · with media' : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}

      {!manage ? <p className="muted">You can see these but not change them.</p> : null}

      {assets === null && !error ? <Spinner /> : null}

      {assets ? (
        <>
          {(['join_invite', 'ad', 'winner_generated'] as const).map((k) => (
            <div className="card" key={k}>
              <h2>
                {KIND_LABELS[k]}
                {k === 'winner_generated' ? <span className="muted"> (latest 2 — auto-sent to the ops group)</span> : null}
              </h2>
              {grouped[k].length === 0 ? (
                <Empty>Nothing uploaded yet.</Empty>
              ) : (
                <div className="posterlist">
                  {grouped[k].map((a) => (
                    <PosterRow
                      key={a.id}
                      asset={a}
                      manage={manage && k !== 'winner_generated'}
                      busy={busy}
                      onToggleActive={() => void toggleActive(a)}
                      onDelete={() => void remove(a)}
                      onSaveCaption={(caption) => void saveCaption(a, caption)}
                      onSaveSchedule={(scheduledAt) => void saveSchedule(a, scheduledAt)}
                      onReplace={(file) => void replaceFile(a, file)}
                      onPostNow={() => void postNow(a)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      ) : null}

      {manage ? (
        <div className="card">
          <h2>Upload</h2>
          <Field label="Kind">
            <select
              className="input"
              value={kind}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setKind(e.target.value as typeof kind)
              }
            >
              <option value="join_invite">Join invite (sent automatically before each Arif round)</option>
              <option value="ad">Ad pool (one picked at random on a schedule)</option>
            </select>
          </Field>
          <Field label="File" hint="JPEG, PNG, GIF, or MP4.">
            <input
              className="input"
              type="file"
              accept="image/jpeg,image/png,image/gif,video/mp4"
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          {/*
            A textarea, not an input: captions are written as several lines —
            a headline, the details, a call to action — and an `<input>` cannot
            hold a newline at all, so pressing Enter did nothing and the whole
            caption arrived in Telegram as one run-on line. Nothing on the way
            out was collapsing them; there was simply no way to type one.
          */}
          <Field
            label="Caption"
            hint="Optional — sent as the Telegram message text alongside the file. Enter starts a new line."
          >
            <textarea
              className="input"
              rows={4}
              value={caption}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setCaption(e.target.value)}
            />
          </Field>
          <div className="actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy || !file}
              onClick={() => void upload()}
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** `datetime-local`'s value shape (no timezone) — local wall-clock time. */
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PosterRow({
  asset,
  manage,
  busy,
  onToggleActive,
  onDelete,
  onSaveCaption,
  onSaveSchedule,
  onReplace,
  onPostNow,
}: {
  asset: MediaAssetView;
  manage: boolean;
  busy: boolean;
  onToggleActive: () => void;
  onDelete: () => void;
  onSaveCaption: (caption: string) => void;
  onSaveSchedule: (scheduledAt: string | null) => void;
  onReplace: (file: File) => void;
  onPostNow: () => void;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [captionDraft, setCaptionDraft] = useState(asset.caption ?? '');
  const [scheduleDraft, setScheduleDraft] = useState(
    asset.scheduledAt ? toDatetimeLocalValue(asset.scheduledAt) : '',
  );
  const [replaceInput, setReplaceInput] = useState<File | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    // Video/GIF get no thumbnail — a <video>/<img> preview isn't worth the
    // extra fetch here; the caption and byte size are enough to identify one
    // in a short list.
    if (asset.contentType === 'image/jpeg' || asset.contentType === 'image/png') {
      api.mediaAssetBlob(asset.id)
        .then((blob) => {
          if (revoked) return;
          objectUrl = URL.createObjectURL(blob);
          setThumbUrl(objectUrl);
        })
        // A thumbnail that can't load (dropped connection, expired session)
        // just keeps the placeholder — the row is still fully usable.
        .catch(() => {});
    }
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, asset.contentType]);

  function openEdit(): void {
    setCaptionDraft(asset.caption ?? '');
    setScheduleDraft(asset.scheduledAt ? toDatetimeLocalValue(asset.scheduledAt) : '');
    setReplaceInput(null);
    setEditing(true);
  }

  return (
    <div className={`posterrow${asset.active ? '' : ' row--off'}`}>
      <div className="posterrow__top">
        {thumbUrl ? (
          <img className="posterrow__thumb" src={thumbUrl} alt="" />
        ) : (
          <div className="posterrow__thumb posterrow__thumb--placeholder">{asset.contentType}</div>
        )}
        <div className="posterrow__meta">
          {/* `pre-wrap` so a multi-line caption reads here the way it will
              arrive in Telegram — collapsed to one line, the list would show
              something the player never sees. */}
          <div className="posterrow__caption">
            {asset.caption ?? <span className="muted">No caption</span>}
          </div>
          <div className="muted small">
            {(asset.byteSize / 1024).toFixed(0)} KB · {asset.uploadedToTelegram ? 'sent before' : 'not sent yet'}
          </div>
          {asset.scheduledAt ? (
            <div className="muted small">Scheduled for {formatDateTime(asset.scheduledAt)}</div>
          ) : null}
        </div>
        {manage ? (
          <div className="actions actions--inline">
            <button type="button" className="button" disabled={busy} onClick={openEdit}>
              {editing ? 'Editing…' : 'Edit'}
            </button>
            <ConfirmButton
              className="button button--primary"
              confirm="Send this poster to every real player and the public channel right now?"
              disabled={busy}
              onConfirm={onPostNow}
            >
              Post now
            </ConfirmButton>
            <ConfirmButton
              className={`button ${asset.active ? '' : 'button--primary'}`}
              confirm={asset.active ? 'Stop using this asset?' : 'Start using this asset?'}
              disabled={busy}
              onConfirm={onToggleActive}
            >
              {asset.active ? 'Deactivate' : 'Activate'}
            </ConfirmButton>
            <ConfirmButton
              className="button button--danger"
              confirm="Delete this asset permanently?"
              disabled={busy}
              onConfirm={onDelete}
            >
              Delete
            </ConfirmButton>
          </div>
        ) : null}
      </div>

      {editing ? (
        <div className="posterrow__edit">
          <Field label="Caption" hint="Enter starts a new line.">
            <textarea
              className="input"
              rows={4}
              value={captionDraft}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setCaptionDraft(e.target.value)}
            />
          </Field>
          <div className="actions actions--inline">
            <button
              type="button"
              className="button button--primary"
              disabled={busy}
              onClick={() => {
                onSaveCaption(captionDraft);
              }}
            >
              Save caption
            </button>
          </div>

          <div className="divider" />

          <Field label="Replace file" hint="JPEG, PNG, GIF, or MP4 — keeps this poster's caption and schedule.">
            <input
              className="input"
              type="file"
              accept="image/jpeg,image/png,image/gif,video/mp4"
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setReplaceInput(e.target.files?.[0] ?? null)}
            />
          </Field>
          <div className="actions actions--inline">
            <ConfirmButton
              className="button button--primary"
              confirm="Replace this poster's file? The old file is gone once replaced."
              disabled={busy || !replaceInput}
              onConfirm={() => {
                if (replaceInput) onReplace(replaceInput);
                setReplaceInput(null);
              }}
            >
              Replace
            </ConfirmButton>
          </div>

          <div className="divider" />

          <Field label="Schedule a send" hint="Pick a date and time, then press OK to seal it.">
            <input
              type="datetime-local"
              className="input"
              value={scheduleDraft}
              disabled={busy}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setScheduleDraft(e.target.value)}
            />
          </Field>
          <div className="actions actions--inline">
            <button
              type="button"
              className="button button--primary"
              disabled={busy || !scheduleDraft}
              onClick={() => {
                onSaveSchedule(scheduleDraft);
              }}
            >
              OK
            </button>
            {asset.scheduledAt ? (
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={() => {
                  setScheduleDraft('');
                  onSaveSchedule(null);
                }}
              >
                Clear schedule
              </button>
            ) : null}
          </div>

          <div className="actions">
            <button type="button" className="button" disabled={busy} onClick={() => setEditing(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
